import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, ShieldCheck, WhatsappLogo, EnvelopeSimple } from "@phosphor-icons/react";
import Footer from "@/components/Footer";

const CANALES = [
  { id: "correo", label: "Correo", Icon: EnvelopeSimple },
  { id: "whatsapp", label: "WhatsApp", Icon: WhatsappLogo },
];

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("solicitar"); // "solicitar" | "canjear"
  const [canal, setCanal] = useState("correo"); // "correo" | "whatsapp"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const esCorreo = canal === "correo";
  const canalNombre = esCorreo ? "correo" : "WhatsApp";
  const fallbackInfo = esCorreo
    ? "Si el correo existe, te enviamos un código de un solo uso a esa dirección."
    : "Si el correo existe y tiene un teléfono registrado, te enviamos un código por WhatsApp.";

  const solicitar = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/forgot-password", {
        email: email.trim().toLowerCase(),
        canal,
      });
      setInfo(data?.detail || fallbackInfo);
      setStep("canjear");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const canjear = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPassword.length < 8) {
      setErr("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErr("La confirmación no coincide con la nueva contraseña.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        new_password: newPassword,
      });
      navigate("/login?motivo=password_restablecida");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <img src="/brand/logotipo.png" alt="CHKR" className="h-10 w-auto mb-8" />
          <div className="label-uppercase mb-3 flex items-center gap-2">
            <ShieldCheck size={14} weight="bold" /> Recuperar acceso
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-ink">
            Recuperar contraseña
          </h1>
          <p className="mt-2 text-slate-600 text-sm">
            Te enviaremos un código de un solo uso. Elige por dónde quieres recibirlo.
          </p>
        </div>

        {step === "solicitar" && (
          <form onSubmit={solicitar} className="space-y-5" data-testid="forgot-password-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-uppercase">Correo</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.mx"
                required
                data-testid="forgot-email"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-uppercase">Enviar código por</Label>
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Canal de envío">
                {CANALES.map(({ id, label, Icon }) => {
                  const activo = canal === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={activo}
                      onClick={() => setCanal(id)}
                      data-testid={`forgot-canal-${id}`}
                      className={`flex items-center justify-center gap-2 h-11 rounded-md border text-sm font-semibold transition-colors ${
                        activo
                          ? "border-brand bg-brand text-white"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      <Icon size={18} weight={activo ? "fill" : "regular"} />
                      {label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500">
                {esCorreo
                  ? "Se enviará a tu correo si la cuenta está activa."
                  : "Requiere un teléfono registrado en tu ficha de empleado."}
              </p>
            </div>
            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="forgot-error">
                {err}
              </div>
            )}
            <Button type="submit" disabled={busy} className="w-full h-11 bg-brand hover:bg-brand-hover text-white font-semibold" data-testid="forgot-submit">
              {busy ? "Enviando…" : `Enviar código por ${esCorreo ? "correo" : "WhatsApp"}`}
            </Button>
          </form>
        )}

        {step === "canjear" && (
          <form onSubmit={canjear} className="space-y-5" data-testid="reset-password-form">
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-md" data-testid="forgot-info">
              {info}{" "}
              {esCorreo
                ? "Si no te llega en unos minutos, revisa tu carpeta de spam o contacta a tu administrador."
                : "Si no te llega en unos minutos, contacta a tu administrador — puede que tu cuenta no tenga un teléfono registrado."}
            </div>
            <div className="space-y-2">
              <Label htmlFor="code" className="label-uppercase">Código recibido por {canalNombre}</Label>
              <Input
                id="code"
                name="one-time-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                required
                data-testid="reset-code"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password" className="label-uppercase">Nueva contraseña</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  name="new-password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  data-testid="reset-new-password"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-ink"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password" className="label-uppercase">Confirmar nueva contraseña</Label>
              <Input
                id="confirm_password"
                name="confirm-new-password"
                type={showPwd ? "text" : "password"}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                data-testid="reset-confirm-password"
                className="h-11"
              />
            </div>
            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="reset-error">
                {err}
              </div>
            )}
            <Button type="submit" disabled={busy} className="w-full h-11 bg-brand hover:bg-brand-hover text-white font-semibold" data-testid="reset-submit">
              {busy ? "Guardando…" : "Restablecer contraseña"}
            </Button>
            <button
              type="button"
              onClick={() => setStep("solicitar")}
              className="w-full text-sm text-slate-500 hover:text-ink"
            >
              ¿No recibiste el código? Solicitar de nuevo
            </button>
          </form>
        )}

        <div className="mt-8 text-center text-sm text-slate-500">
          <Link to="/login" className="font-semibold text-ink hover:underline">
            Volver a iniciar sesión
          </Link>
        </div>
        <Footer className="mt-10" />
      </div>
    </div>
  );
}
